import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useMyOrgs, useMyInvites, acceptInvite } from '../data/entities';
import { apiPost } from '../utils/backendApi';
import CheckoutIntent from '../components/CheckoutIntent';
import OrgStart from '../components/OrgStart';
import { intentCopy } from '../data/plans';

// Console entry point: every org the signed-in user belongs to, plus any
// pending invites addressed to their email. Org creation is server-side
// (POST /orgs — verified email, company-domain policy, audit); the owner
// membership arrives with it and useMyOrgs reacts.
export default function Orgs() {
  const { user } = useAuth();
  const orgs = useMyOrgs();
  const invites = useMyInvites();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const plan = params.get('plan');
  const intent = intentCopy(plan);
  const [error, setError] = useState('');

  // Arriving from an invite email (/orgs?invite=<token>): accept it without
  // another click once the signed-in email matches a pending invite.
  const inviteToken = params.get('invite');
  const autoAccepted = useRef(false);
  const [joining, setJoining] = useState(null); // orgName while the worker seats us
  useEffect(() => {
    if (!inviteToken || autoAccepted.current || !invites) return;
    const inv = invites.find((i) => i.id === inviteToken);
    if (!inv) return;
    autoAccepted.current = true;
    setJoining(inv.orgName || inv.orgId);
    acceptInvite(inviteToken).catch((e) => { setJoining(null); setError(e.message); });
  }, [inviteToken, invites]);
  // The membership arrives via the claims worker a moment after accept —
  // then go straight into the organization instead of leaving the user on
  // this list wondering whether anything happened (2026-10-12 audit B4).
  useEffect(() => {
    if (!joining || !orgs) return;
    const org = orgs.find((o) => o.name === joining || o.id === joining);
    if (org) { setJoining(null); navigate(`/orgs/${org.id}`); }
  }, [joining, orgs, navigate]);
  // Same for a freshly created organization (its membership is in the batch).
  const [createdId, setCreatedId] = useState(null);
  useEffect(() => {
    if (createdId && orgs && orgs.some((o) => o.id === createdId)) navigate(`/orgs/${createdId}`);
  }, [createdId, orgs, navigate]);

  const hasInvites = !!(invites && invites.length);
  // Arrived from an invite email: the accept is in flight, or the worker is
  // seating us — don't offer "create your first organization" meanwhile.
  const waitingOnInvite = !!joining || (!!inviteToken && invites === null);

  return (
    <div className="cs-page" data-nova-note="Organizations: every organization you belong to with your seat; Create organization opens a new console.">
      <CheckoutIntent />
      <h1 style={{ margin: '16px 0 0' }}>Organizations</h1>
      <p className="cs-help" style={{ marginTop: 6 }}>
        Workspaces, projects, agents, pipelines and compute all live inside an
        organization. You can belong to several.
      </p>

      {joining && (
        <div className="cs-card" style={{ padding: 14, marginTop: 16 }}>
          Joining <strong style={{ color: 'var(--text)' }}>{joining}</strong>…
        </div>
      )}

      {hasInvites && (
        <div className="cs-card" style={{ padding: 14, marginTop: 16 }}>
          <strong style={{ color: 'var(--text)' }}>You've been invited</strong>
          {invites.map((inv) => (
            <div key={inv.id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
              <span>
                {inv.orgName || inv.orgId} — as <em>{inv.role}</em>
                {inv.workspaceId && <> · workspace <em>{inv.workspaceName || inv.workspaceId}</em> as <em>{inv.workspaceRole || 'member'}</em></>}
              </span>
              <span style={{ flex: 1 }} />
              <button
                className="cs-btn cs-btn-primary cs-btn-sm"
                onClick={async () => {
                  try {
                    setJoining(inv.orgName || inv.orgId);
                    await acceptInvite(inv.id);
                  } catch (e) {
                    setJoining(null);
                    setError(e.message);
                  }
                }}
              >
                Join
              </button>
            </div>
          ))}
          <p className="cs-help" style={{ marginTop: 10 }}>
            Join the organization you were invited to — you don't need to create one.
          </p>
        </div>
      )}

      {/* Create (or, when a company has verified this email's domain,
          request to join) — components/OrgStart.js decides by domain. */}
      {user && !hasInvites && !waitingOnInvite && (
        <OrgStart
          onCreated={setCreatedId}
          referral={params.get('ref') ? async (id) => {
            try { await apiPost('/billing/referral', { orgId: id, code: params.get('ref') }); } catch { /* unknown codes are ignored */ }
          } : null}
        />
      )}
      {error && <p className="cs-help" style={{ color: 'var(--danger, #e5484d)', marginTop: 8 }}>{error}</p>}

      {orgs === null && <p className="cs-help" style={{ marginTop: 20 }}>Loading…</p>}
      {orgs && !orgs.length && !hasInvites && !waitingOnInvite && (
        <div className="cs-empty" style={{ marginTop: 20 }}>
          No organizations yet — create your first one above, or join the one your company already has.
        </div>
      )}

      <div style={{ display: 'grid', gap: 10, marginTop: 18 }}>
        {(orgs || []).map((org) => (
          <Link
            key={org.id}
            to={intent?.next ? `/orgs/${org.id}/${intent.next}?plan=${plan}` : `/orgs/${org.id}`}
            className="cs-card"
            style={{ padding: 14, textDecoration: 'none' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <strong style={{ color: 'var(--text)' }}>{org.name}</strong>
              <span className="cs-help" style={{ fontSize: 12 }}>{org.myRole}</span>
              <span style={{ flex: 1 }} />
              {org.subscription?.planName && (
                <span className="cs-help" style={{ fontSize: 12 }}>{org.subscription.planName}</span>
              )}
            </div>
            {org.description && <p className="cs-help" style={{ marginTop: 6 }}>{org.description}</p>}
          </Link>
        ))}
      </div>
    </div>
  );
}
